import { For, location, onMount, reactive } from 'cdui-js';
import { replaceTemplate } from '@china-difi/lib/template';
import i18n from '../../../i18n';
import { DiFiCheckBox } from '../../../components/DiFi/DiFiCheckBox';
import { DiFiNoData } from '../../../components/DiFi/DiFiNoData';
import { batchSaveStockTransferInStocks, type StockTransferInStockItem } from '../../../api/stockTransferIn';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { showModal } from '../components/Modal';
import { showToast, showToastError } from '../components/Toast';
import { goBackByBrowser, routeReplace } from '../../../lib/browser';
import { AddStockPill } from './AddStockPill';
import { StockTransferNav } from './Nav';
import {
  formatStockLineLabel,
  formatStockQuantity,
  getStockTransferApplyId,
  getStockTransferDraft,
  getStockTransferLines,
  mergeStockLinesWithBatchSaveResp,
  readMarketQuery,
  setStockTransferDraft,
  setStockTransferLines,
  syncStockTransferAppShell,
  withStockTransferQuery,
  type StockTransferLineItem,
  type StockTransferMarket,
} from './shared';
import './page.css';

const IconEdit = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5.47852 19.2715H18.5215" stroke="currentColor" stroke-miterlimit="10" stroke-linejoin="round" />
    <path
      d="M15.1755 11.1079L17.8589 8.35674L14.2585 4.90918L11.4996 7.68676L6.71805 12.5006L6.14062 16.7803L10.4543 15.9482L12.8149 13.528L15.1755 11.1079ZM11.4996 7.68676L12.6621 10.123L15.1755 11.1079"
      stroke="currentColor"
      stroke-linejoin="round"
    />
  </svg>
);

const IconTrash = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6.21875 6.60352V19.7088H17.7822V6.60352H6.21875Z" stroke="currentColor" stroke-linejoin="round" />
    <path
      d="M10.4582 10.458V15.4688M13.5418 10.458V15.4688M4.29102 6.60352H19.709"
      stroke="currentColor"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path d="M8.91602 6.60371L10.1838 4.29102H13.8409L15.0832 6.60371H8.91602Z" stroke="currentColor" stroke-linejoin="round" />
  </svg>
);

export const StockTransferInStocks = () => {
  const state = reactive({
    market: 'HK' as StockTransferMarket,
    lines: [] as StockTransferLineItem[],
    saving: false,
  });

  const syncLines = () => {
    state.lines = getStockTransferLines().slice();
  };

  const hasSelected = () => state.lines.some((item) => item.selected);

  const toggleLine = (id: string) => {
    state.lines = state.lines.map((item) => (item.id === id ? { ...item, selected: !item.selected } : item));
    setStockTransferLines(state.lines);
  };

  const removeLine = (id: string) => {
    state.lines = state.lines.filter((item) => item.id !== id);
    setStockTransferLines(state.lines);
  };

  const confirmRemoveLine = (id: string) => {
    const line = state.lines.find((item) => item.id === id);
    if (!line) return;
    showModal({
      title: i18n.StockTransferIn.DeleteStockTitle,
      content: () =>
        replaceTemplate(i18n.StockTransferIn.DeleteStockConfirm, {
          label: formatStockLineLabel(line),
        }),
      cancelText: i18n.FundH5.No,
      confirmText: i18n.FundH5.Yes,
      onConfirm: () => removeLine(id),
    });
  };

  const goAdd = (lineId?: string) => {
    const extra: Record<string, string> = { market: state.market };
    if (lineId) extra.lineId = lineId;
    location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/stocks/add', extra), [0, 0]);
  };

  const onNext = async () => {
    if (state.saving) return;
    if (!hasSelected()) {
      showToast(i18n.StockTransferIn.PleaseSelectStock);
      return;
    }
    const draft = getStockTransferDraft();
    const applyId = getStockTransferApplyId();
    if (!draft || !applyId) {
      showToast(i18n.StockTransferIn.LoadFail);
      return;
    }
    const selected = state.lines.filter((item) => item.selected);
    state.saving = true;
    try {
      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试
      }
      const saved = await batchSaveStockTransferInStocks({
        applyId: String(applyId),
        stocks: selected.map((item): StockTransferInStockItem => {
          const row: StockTransferInStockItem = {
            stockCode: item.stockCode,
            marketId: item.marketId,
            quantity: Math.floor(Number(item.quantity)),
          };
          const name = item.stockName?.trim();
          if (name) row.stockName = name;
          return row;
        }),
      });
      const merged = mergeStockLinesWithBatchSaveResp(state.lines, selected, saved);
      state.lines = merged;
      setStockTransferLines(merged);
      setStockTransferDraft({ ...draft, stocks: merged });
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/confirm', { market: state.market }), [0, 0]);
    } catch (e) {
      showToastError(e);
    } finally {
      state.saving = false;
    }
  };

  const onClose = () => {
    routeReplace(withStockTransferQuery('/h5/stock-transfer-in'));
  };

  onMount(() => {
    syncStockTransferAppShell();
    state.market = readMarketQuery();
    if (!getStockTransferDraft()) {
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/select-broker', { market: state.market }), [0, 0]);
      return;
    }
    syncLines();
  });

  return (
    <div class="stock-transfer-in-h5">
      <StockTransferNav
        title={i18n.StockTransferIn.AddStockInfoTitle}
        onBack={goBackByBrowser}
        onClose={onClose}
      />
      <div class={`sti-scroll${state.lines.length > 0 ? '' : ' sti-scroll-empty'}`}>
        {state.lines.length === 0 ? (
          <div class="sti-stocks-empty">
            <DiFiNoData hasIcon text={i18n.StockTransferIn.StocksEmpty} style={{ padding: '0' }} />
            <AddStockPill onclick={() => goAdd()} />
          </div>
        ) : (
          <div class="sti-stocks-list-wrap">
            <For each={state.lines}>
              {(item) => (
                <div class="sti-stock-row">
                  <DiFiCheckBox
                    type="checkbox"
                    class="sti-stock-check"
                    value={item.id}
                    checked={item.selected}
                    activeColor="#1060FF"
                    defaultColor="#E4E4E4"
                    onChange={() => toggleLine(item.id)}
                  />
                  <div class="sti-stock-row-body">
                    <div class="sti-stock-row-head">
                      <div
                        class="sti-stock-row-title sti-stock-row-selectable"
                        role="button"
                        tabindex={-1}
                        onclick={() => toggleLine(item.id)}
                      >
                        {formatStockLineLabel(item)}
                      </div>
                      <div class="sti-stock-row-actions">
                        <button type="button" class="sti-stock-icon-btn" onclick={() => goAdd(item.id)}>
                          <IconEdit />
                        </button>
                        <button type="button" class="sti-stock-icon-btn" onclick={() => confirmRemoveLine(item.id)}>
                          <IconTrash />
                        </button>
                      </div>
                    </div>
                    <div
                      class="sti-stock-row-qty sti-stock-row-selectable"
                      role="button"
                      tabindex={-1}
                      onclick={() => toggleLine(item.id)}
                    >
                      <span class="sti-stock-row-qty-label">{i18n.StockTransferIn.StockQuantityLabel}</span>
                      <span class="sti-stock-row-qty-value">{formatStockQuantity(item.quantity)}</span>
                    </div>
                  </div>
                </div>
              )}
            </For>
            <AddStockPill class="sti-add-stock-btn-inline" onclick={() => goAdd()} />
          </div>
        )}
      </div>
      {state.lines.length > 0 ? (
        <div class="sti-footer">
          <button
            type="button"
            class={`primary-btn btn-rounded width-100${hasSelected() && !state.saving ? '' : ' sti-btn-disabled'}`}
            disabled={!hasSelected() || state.saving}
            onclick={onNext}
          >
            {state.saving ? i18n.StockTransferIn.Submitting : i18n.StockTransferIn.NextStep}
          </button>
        </div>
      ) : null}
    </div>
  );
};
